import { useState } from 'react';
import type { Address } from 'viem';
import { useStableCallback } from '@soundxyz/graphql-react-query/utils';
import { ContractMethod } from '@soundxyz/utils/const';
import { safeTransferFromAbi } from '@abi/safeTransferFrom';
import { useConnectedAuth } from '@context/auth/connected';
import { useToast } from '@context/toast';
import { Transaction } from '@context/transaction';
import { getTransactionResponse } from '@context/transaction/TransactionStatusStore';
import { chainFromId } from '@utils/chain';
import { useConnectedWallet } from './useConnectedWallet';

export const useTransferToken = ({
  chainId,
  contractAddress,
  releaseId,
  tokenId,
  transferToAddress,
}: {
  chainId: number;
  contractAddress: Address;
  releaseId: string;
  tokenId: string;
  transferToAddress: Address;
}) => {
  const [isTransferring, setIsTransferring] = useState(false);
  const { openToast } = useToast();

  const { getSigner, expectMatchingChain, walletAddress, provider, getDeepLink } =
    useConnectedAuth();
  const { handleTransaction } = Transaction.useContainer();

  const onGiftHandle = useStableCallback(async () => {
    const chain = expectMatchingChain(chainId);
    if (chain.stop) {
      setIsTransferring(false);
      return null;
    }

    try {
      if (chain.switching) await chain.switching;
    } catch (err) {
      setIsTransferring(false);
      return null;
    }

    const signer = await getSigner(chainId);
    const chainProvider = provider(chainId);

    /**
     * This is critical for a better mobile UX.
     * We have to set before the actual transaction is sent.
     * This way it can open the user's wallet app and make it a seamless flow
     */
    const deepLink = getDeepLink();
    if (deepLink) {
      window.location.href = deepLink;
    }

    try {
      const txHash = await signer.writeContract({
        address: contractAddress,
        abi: safeTransferFromAbi,
        functionName: 'safeTransferFrom',
        args: [walletAddress as Address, transferToAddress, tokenId],
        chain: chainFromId(chainId),
      });
      return getTransactionResponse({
        hash: txHash,
        chainId,
        provider: chainProvider,
      });
    } catch (error) {
      setIsTransferring(false);
      openToast({ text: 'Something went wrong. Please try again.', variant: 'error' });
      return null;
    }
  });

  const transferToken = useConnectedWallet({
    async onAuth() {
      setIsTransferring(true);
      await handleTransaction({
        contractMethod: ContractMethod.COLLECTOR__TRANSFER_EDITION,
        transactionFn: onGiftHandle,
        chainId,
        openProcessingModal: true,
        showOpenWalletToast: false,
        releaseId,
      });
    },
  });

  return { transferToken, isTransferring, setIsTransferring };
};
